import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider } from '@mui/material';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me/hooks';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HorizontalBox } from '../../component/layout/BoxLayout';
import { getBenefitInfoText } from '../../extension/interface/benefit-info-text.extension.interface';

interface Props {
  benefit?: BenefitDTOBenefitEnum;
  customBenefitTitle?: string;
  customBenefitKey?: string;
  overrideKey?: string;
}

const managerGeneralLink = 'https://www.youtube.com/watch?v=kvwL7VrPHqM';
const appGeneralLink = 'https://www.youtube.com/watch?v=ES9z_VN-HaQ';

const BenefitInfoText: React.FC<Props> = ({ benefit, customBenefitTitle, customBenefitKey, overrideKey }) => {
  const { tenant } = useTenant();
  const { t } = useTranslation();
  const lunchDailyLimit = tenant?.taxConfig && tenant.taxConfig.lunchNonCashValue + tenant.taxConfig.lunchSupplement;

  // Get the benefit-specific extension if available
  const benefitInfoTextExtension = benefit ? getBenefitInfoText(benefit) : undefined;
  const benefitLinks = benefitInfoTextExtension?.getBenefitLinks();
  const benefitMoreLinks = benefitInfoTextExtension?.getBenefitMoreLinks();
  const helpCenterLink = benefitInfoTextExtension?.getHelpCenterLink();

  const benefitName = t(`benefitsModule:${benefit}.title`);

  const extensionProps = {
    benefit,
    hasNonCashBenefits: !!tenant?.nonCashBenefits.length,
  };

  return (
    <>
      <Accordion
        sx={{
          marginBottom: 1.5,
          borderRadius: 1,
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <HorizontalBox alignItems="center">
            <InfoOutlinedIcon sx={{ marginRight: 1 }} />
            {customBenefitTitle ? customBenefitTitle : t(`benefitsModule:${benefit}.title`)}
          </HorizontalBox>
        </AccordionSummary>
        <AccordionDetails>
          {customBenefitKey ? (
            <Trans i18nKey={`customBenefitsModule:benefits.${customBenefitKey}.infoTextFull`} />
          ) : (
            <Trans
              i18nKey={overrideKey ?? `benefitsModule:${benefit}.infoTextFull`}
              values={{
                ...(lunchDailyLimit && {
                  lunchDailyLimit: lunchDailyLimit,
                  lunchMonthlyLimit: lunchDailyLimit * 15,
                }),
                ...(tenant?.taxConfig && {
                  restaurantLimit: tenant.taxConfig.atLunchRestaurantDailyLimit,
                  supermarketLimit: tenant.taxConfig.atLunchSupermarketDailyLimit,
                }),
              }}
              components={{
                risLink: (
                  <a
                    href="https://www.ris.bka.gv.at/Ergebnis.wxe?Abfrage=Bundesnormen&Kundmachungsnummer=400%2F1988&VonParagraf=26&FassungVom=15.12.2023"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                findokLink: (
                  <a
                    href="https://findok.bmf.gv.at/findok/volltext(suche:Standardsuche)?segmentId=bff95e26-cfbd-4cc2-9489-ee8b42365ccf"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                settingsLink: <Link to="/settings/fiscalYear" />,
              }}
            >
              <Divider />
            </Trans>
          )}
          {benefit && benefitLinks && (benefitLinks.managerSpecific || benefitLinks.appSpecific) && (
            <Box>
              <strong>{t('benefitInfoText:videosTitle')}</strong>
              <br />
              {t('benefitInfoText:videosDescription')}
              <br />
              <ul>
                {!benefitLinks.hideGeneral && (
                  <li>
                    <a target="_blank" href={managerGeneralLink} rel="noreferrer">
                      {t('benefitInfoText:managerGeneral')}
                    </a>
                  </li>
                )}
                {benefitLinks.managerSpecific && (
                  <li>
                    <>
                      {benefitLinks.managerSpecificLabel ? <>{t(`benefitsModule:${benefit}.infoTextVideos.managerSpecific`)}: </> : null}
                      <a target="_blank" href={benefitLinks.managerSpecific} rel="noreferrer">
                        {benefitLinks.managerSpecificLabel ?? t(`benefitsModule:${benefit}.infoTextVideos.managerSpecific`)}
                      </a>
                    </>
                  </li>
                )}
                {!benefitLinks.hideGeneral && (
                  <li>
                    <a target="_blank" href={appGeneralLink} rel="noreferrer">
                      {t('benefitInfoText:appGeneral')}
                    </a>
                  </li>
                )}
                {benefitLinks.appSpecific && (
                  <li>
                    <>
                      {benefitLinks.appSpecificLabel ? <>{t(`benefitsModule:${benefit}.infoTextVideos.appSpecific`)}: </> : null}
                      <a target="_blank" href={benefitLinks.appSpecific} rel="noreferrer">
                        {benefitLinks.appSpecificLabel ?? t(`benefitsModule:${benefit}.infoTextVideos.appSpecific`)}
                      </a>
                    </>
                  </li>
                )}
              </ul>
            </Box>
          )}
          {benefit && benefitMoreLinks?.title && benefitMoreLinks.link && (
            <Box>
              <strong>
                {t('benefitInfoText:videosMoreTitle')} {benefitName}
              </strong>
              <br />
              <ul>
                <li>
                  <a target="_blank" href={benefitMoreLinks.link} rel="noreferrer">
                    {benefitMoreLinks.title}
                  </a>
                </li>
              </ul>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'end', paddingRight: 1 }}>
            {benefit && helpCenterLink && (
              <a href={helpCenterLink} target="_blank" rel="noreferrer">
                {t('benefitsModule:CUSTOM.fields.comment')}
              </a>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      {benefitInfoTextExtension?.renderAdditionalContent(extensionProps)}
    </>
  );
};

export default BenefitInfoText;
